import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ConnectedFormContext, { IConnectedFormModalProps } from '../../../components/Context/ConnectedFormContext';
import ModalContext from '../../../components/Context/ModalContext';
import DocumentModal2 from '../../../components/modals/DocumentModal2';
import { IAfterPostSave, IAfterSubmit } from '../../../components/connectedForm/useManagedSubmit';
import { ActionType, IMetaAction, ISalesOrder, Maybe, ReportType } from '../../../graphql/schema/p4types';
import { useDocumentConfig } from '../../../utils/FetchConfig';
import ShortageModal from './ShortageModal';
import { EntityType } from '../../../@types/entityTypes';
import PickingInstruction from '../../../graphql/entities/sales/PickingInstruction';
import Acknowledgement from '../../../graphql/entities/sales/Acknowledgement';
import QuotationDocument from '../../../graphql/entities/sales/QuotationDocument';
import ProformaInvoice from '../../../graphql/entities/sales/ProformaInvoice';
import Payment from '../../../graphql/entities/sales/Payment';
import BatchAllocationModal from './BatchAllocationModal';
import { useDispatch, useSelector } from 'react-redux';
import SalesOrder from '../../../graphql/entities/sales/SalesOrder';
import { change } from '../../../components/connectedForm/utils/actions';
import generateClientMutationId from '../../../graphql/schema/generateClientMutationId';
import { IActionType } from '../../../components/connectedForm/Hooks/usePerformSave';
import AssemblyShortageModal from './AssemblyShortageModal';
import { AssemblyPrintSource, useP4DocumentController } from '../../../components/document/DocumentProvider';
import { IReduxStore } from '../../../@types/reduxTypes';
import { useFullUserDetails } from '../../../utils/hooks';

//Used to sort the actions so we process them in the expected order.
const modalOrderMap: { [key in ActionType]?: number } = {
  [ActionType.RaiseShortageFromOrder]: 1,
  [ActionType.SalesOrderBatchAllocation]: 2,
  [ActionType.PrintAcknowledgement]: 3,
  [ActionType.PrintQuotation]: 4,
  [ActionType.PrintProformaInvoice]: 5,
  [ActionType.PrintPaymentReceipt]: 6,
  [ActionType.PrintPickingInstruction]: 7,
  [ActionType.RaiseAssemblyShortageFromOrder]: 8,
  [ActionType.PrintAssemblyPickingList]: 9,
};

const actionComparator: (a: IMetaAction, b: IMetaAction) => number = (a, b) => {
  const aOrder = (a.actionType && modalOrderMap[a.actionType]) || -1;
  const bOrder = (b.actionType && modalOrderMap[b.actionType]) || -1;
  return aOrder - bOrder;
};

const CustomerDocumentModal = ({
  entity,
  modalOpen,
  delayBeforeSkip,
}: {
  entity: EntityType;
  modalOpen: boolean;
  delayBeforeSkip: boolean;
}) => {
  const connectedForm = useContext(ConnectedFormContext);
  const { onClose } = useContext(ModalContext);

  return (
    <DocumentModal2
      modalOpen={modalOpen}
      values={connectedForm.values}
      onClose={onClose}
      entity={entity}
      useCustomerDefaults={true}
      delayBeforeSkip={delayBeforeSkip}
      modalStyles={{
        layer: {
          root: { zIndex: 1000000 },
        },
      }}
    />
  );
};
const PickingInstructionModal = ({
  modalOpen,
  metaAction,
  delayBeforeSkip,
}: IConnectedFormModalProps & {
  delayBeforeSkip: boolean;
}) => {
  const connectedForm = useContext(ConnectedFormContext);

  return (
    <DocumentModal2
      modalOpen={modalOpen}
      metaAction={metaAction}
      {...connectedForm}
      generateSingle={true}
      key={'pickingInstructions'}
      entity={PickingInstruction}
      allowEntityEmail={false}
      delayBeforeSkip={delayBeforeSkip}
      modalStyles={{
        layer: {
          root: { zIndex: 1000000 },
        },
      }}
    />
  );
};

type IPostSaveModalState = { action?: IExtendedMetaAction };

const SalesOrderPostSaveModal = ({
  postSaveModalState,
  isFirstAction,
  onCompleteAction,
  addActions,
  lastLegacyAction,
}: {
  postSaveModalState: IPostSaveModalState;
  isFirstAction: boolean;
  onCompleteAction: (closeModals: boolean) => void;
  addActions: (actions: IExtendedMetaAction[]) => void;
  doDelayedPostSave?: boolean;
  lastLegacyAction?: (actions: IExtendedMetaAction[]) => void;
}) => {
  const documentConfig = useDocumentConfig();
  const fullUserDetails = useFullUserDetails();

  let modal;
  //This if statement is required so we don't re-process this when we 'clear' the postSaveModalState (I.e. on close of a modal).
  let doDelayedPostSave = false;
  if (postSaveModalState.action?.actionType) {
    switch (postSaveModalState.action.actionType) {
      case ActionType.RaiseShortageFromOrder:
        if (lastLegacyAction == ActionType.RaiseShortageFromOrder) {
          doDelayedPostSave = true;
        }
        modal = (
          <ShortageModal
            modalOpen={true}
            metaAction={postSaveModalState.action}
            addActions={addActions}
            doDelayedPostSave={doDelayedPostSave}
          />
        );
        break;
      case ActionType.RaiseAssemblyShortageFromOrder:
        if (lastLegacyAction == ActionType.RaiseAssemblyShortageFromOrder) {
          doDelayedPostSave = true;
        }
        modal = (
          <AssemblyShortageModal
            modalOpen={fullUserDetails?.flags?.assembliesEnabled}
            metaAction={postSaveModalState.action}
            addActions={addActions}
            doDelayedPostSave={doDelayedPostSave}
          />
        );
        break;
      case ActionType.SalesOrderBatchAllocation:
        modal = <BatchAllocationModal modalOpen={true} metaAction={postSaveModalState.action} />;
        break;
      case ActionType.PrintAcknowledgement:
        /**
         * @deprecated - Note that this should go via DocumentCarousel now.
         *             - To be removed in a future update (Along with the Carousel Rox flag).
         */
        modal = <CustomerDocumentModal modalOpen={true} entity={Acknowledgement} delayBeforeSkip={!isFirstAction} />;
        break;
      case ActionType.PrintQuotation:
        /**
         * @deprecated - Note that this should go via DocumentCarousel now.
         *             - To be removed in a future update (Along with the Carousel Rox flag).
         */
        modal = <CustomerDocumentModal modalOpen={true} entity={QuotationDocument} delayBeforeSkip={!isFirstAction} />;
        break;
      case ActionType.PrintProformaInvoice:
        /**
         * @deprecated - Note that this should go via DocumentCarousel now.
         *             - To be removed in a future update (Along with the Carousel Rox flag).
         */
        modal = <CustomerDocumentModal modalOpen={true} entity={ProformaInvoice} delayBeforeSkip={!isFirstAction} />;
        break;
      case ActionType.PrintPaymentReceipt:
        /**
         * @deprecated - Note that this should go via DocumentCarousel now.
         *             - To be removed in a future update (Along with the Carousel Rox flag).
         */
        modal = <CustomerDocumentModal modalOpen={true} entity={Payment} delayBeforeSkip={!isFirstAction} />;
        break;
      case ActionType.PrintPickingInstruction:
        /**
         * @deprecated - Note that this should go via DocumentCarousel now.
         *             - To be removed in a future update (Along with the Carousel Rox flag).
         */
        if (documentConfig?.pickingInstructionDefault?.documentsInUse) {
          modal = (
            <PickingInstructionModal
              modalOpen={true}
              metaAction={postSaveModalState.action}
              delayBeforeSkip={!isFirstAction}
            />
          );
        }
        break;
      default:
        onCompleteAction(false);
    }
  }

  return <ModalContext.Provider value={{ onClose: onCompleteAction }}>{modal}</ModalContext.Provider>;
};

export type IExtendedMetaAction = IMetaAction & {
  postSave?: boolean; //Indicates if this was triggered by entityMetaData as a postSave or otherwise.
  preSave?: boolean; //Indicates this is being triggered immediately prior to completion (Trade Counter).
  options?: {
    rowIdx?: Maybe<number>;
  };
};

/**
 * Handler for post-save behaviours for SalesOrderForm.
 *
 * Separated from ConnectedForm handling in P4-6142 to make it easier for us to manipulate and change the order of display of elements
 * and to make it easier for us to check modal states by other Sales Order logic (E.g. Avoid a 'line' triggered modal from staying open
 * if postSave modals have been triggered).
 */
export const useSalesOrderPostSave: () => {
  postSaveFunction: IAfterPostSave;
  modals: ReactElement;
  addModalActions: (actions: Array<IExtendedMetaAction>) => void;
} = () => {
  const dispatch = useDispatch();
  const [afterPostSave, setAfterPostSave] = useState<IAfterSubmit | undefined>();
  const [result, setResult] = useState<any>(); //Just used to hold the result for passing through to afterPostSave later.

  const [modalState, setModalState] = useState<IPostSaveModalState>({});
  const [actions, setActions] = useState<Array<IExtendedMetaAction>>([]);
  const [nextActionIndex, setNextActionIndex] = useState(-1);
  const [lastLegacyAction, setLastLegacyAction] = useState<ActionType>();

  const onCompleteAction = useCallback((closeModals: boolean = true) => {
    if (closeModals) {
      setModalState({});
    }
    setNextActionIndex((actionIndex) => actionIndex + 1);
  }, []);

  useEffect(() => {
    if (nextActionIndex >= 0) {
      if (nextActionIndex < actions.length) {
        const action = actions[nextActionIndex];
        setModalState({ action });
      } else {
        setNextActionIndex(-1);
        afterPostSave?.({ result });
      }
    }
    //Disable exhaustive deps - Explicitly only wish to process this effect when the action index is updated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextActionIndex]);

  //Bind a change function to the SO Form so we can submit changes (This hook is called from outside the connected form).
  const boundChange = useCallback(
    (field, value, mutationId) => change(SalesOrder.type, field, value, false, mutationId),
    []
  );

  const enableDocumentCarousel = useSelector((store: IReduxStore) => {
    return store.app.fullUserDetails?.flags?.documentCarouselEnabled ?? false;
  });
  const { printDocumentSingle } = useP4DocumentController();
  const salesOrderPostSave: IAfterPostSave = useCallback(
    (afterPostSave, { action: submitAction, result }: { action: IActionType; result: any }): void => {
      //Note - Since we're setting a function into state, need to have the function wrapper like this, or it see's it as a state
      //     - setter and calls the function immediately, which is not the intent here.
      setAfterPostSave(() => afterPostSave);
      setResult(result);

      const entityMetaData = (result as Maybe<ISalesOrder>)?.entityMetaData;

      if (entityMetaData?.actions && Array.isArray(entityMetaData.actions) && entityMetaData.actions.length > 0) {
        if (!submitAction.submitMeta?.suppressActions) {
          const newActions = (entityMetaData.actions.filter((action) => !!action?.actionType) as Array<IMetaAction>)
            .sort(actionComparator)
            .map((action) => ({ ...action, postSave: true }));

          const legacyActions: typeof newActions = [];
          newActions.forEach((action) => {
            if (enableDocumentCarousel) {
              //If document carousel feature enabled, dispatch print actions to the DocumentProvider rather than handling them here.
              switch (action.actionType) {
                case ActionType.PrintAcknowledgement:
                  printDocumentSingle(ReportType.SalesAcknowledgement, result.id);
                  break;
                case ActionType.PrintQuotation:
                  printDocumentSingle(ReportType.SalesQuote, result.id);
                  break;
                case ActionType.PrintProformaInvoice:
                  printDocumentSingle(ReportType.ProformaInvoice, result.id);
                  break;
                case ActionType.PrintPaymentReceipt:
                  printDocumentSingle(ReportType.PaymentReceipt, result.id);
                  break;
                case ActionType.PrintPickingInstruction:
                  printDocumentSingle(ReportType.SalesPickingList, result.id);
                  break;
                case ActionType.PrintAssemblyPickingList:
                  printDocumentSingle(ReportType.AssemblyPickingList, result.id, {
                    metaData: { source: AssemblyPrintSource.SalesOrder },
                  });
                  break;
                default:
                  legacyActions.push(action);
              }
            } else {
              legacyActions.push(action);
            }
          });

          setActions(legacyActions);
          if (legacyActions.length > 0) {
            let tempLastLegacyAction = legacyActions[0]?.actionType;
            if (legacyActions.length > 1) {
              for (let i = 1; i < legacyActions.length; i++) {
                if (
                  legacyActions[i]?.actionType &&
                  modalOrderMap[legacyActions[i].actionType] > modalOrderMap[tempLastLegacyAction]
                ) {
                  tempLastLegacyAction = legacyActions[i]?.actionType;
                }
              }
            }

            setLastLegacyAction(tempLastLegacyAction);
          }
          setNextActionIndex(0);
        }
        //Now that we've picked up the actions to process, clear it from props.values, otherwise other behaviours (like temporary saves) are blocked following this.
        dispatch(boundChange('entityMetaData.actions', null, generateClientMutationId()));
      } else {
        //Nothing to process here right now, continue on to 'afterSubmit' logic.
        afterPostSave?.({ result });
      }
    },
    [boundChange, dispatch, printDocumentSingle, enableDocumentCarousel]
  );

  //Directly add new actions to the actions array to be processed.
  const addActions = useCallback((newActions: IExtendedMetaAction[]) => {
    setActions((existingActions) => [...existingActions, ...newActions]);
  }, []);

  //Allow adding of actions via non-save means (Allow batch allocation/shortages to use the same modals as post-save by setting actions).
  //If there are any outstanding actions, these are kept in the new array to be processed first.
  const addModalActions = useCallback(
    (actions: Array<IMetaAction>) => {
      setActions((existingActions) => {
        const unprocessedActions =
          nextActionIndex < existingActions.length ? existingActions.slice(nextActionIndex) : [];
        return [...unprocessedActions, ...actions];
      });
      setNextActionIndex(0);
      setAfterPostSave(undefined); //Clear 'afterPostSave' so we don't accidentally navigate or similar when using this capability.
    },
    [nextActionIndex]
  );

  //Memoising result to be sure we don't cause any more renders than necessary.
  return useMemo(() => {
    return {
      postSaveFunction: salesOrderPostSave,
      modals: (
        <SalesOrderPostSaveModal
          key={'postSaveModal'}
          postSaveModalState={modalState}
          isFirstAction={nextActionIndex === 0}
          lastLegacyAction={lastLegacyAction}
          onCompleteAction={onCompleteAction}
          addActions={addActions}
        />
      ),
      addModalActions,
    };
  }, [salesOrderPostSave, modalState, nextActionIndex, onCompleteAction, addActions, addModalActions]);
};
